import { mapFromCognitoParams, mapToCognitoParams } from '../../services';

import AmplifyActions from './Amplify.actions';
import AuthActions from '../Auth.actions';
import { DEFAULT_MFA_OPTIONS } from './Amplify.constants';
import { filterUnique } from '/b2b/common/helpers/Array';
import { genericReducer } from '/b2b/common/helpers/Redux/genericReducer';

/** @typedef {import('aws-amplify/auth').SignInOutput['nextStep'] & import('aws-amplify/auth').ResetPasswordOutput['nextStep']} AmplifyNextStep */

export const initialState = {
    /**
     * Additional data that is returned from the Auth API. Usually contains information about the user's account status.
     * @type {AmplifyNextStep['additionalInfo']|null}
     */
    additionalInfo: null,
    /**
     * The list of MFA options that the user has setup
     * @type {Extract<AmplifyNextStep, { signInStep: 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' }>['allowedMFATypes']}
     */
    allowedMFATypes: null,
    /**
     * The list of sign-in challenges that are available for the User.
     * In `USER_SRP_AUTH` (default) mode these are 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'SELECT_MFA_TYPE' | 'MFA_SETUP';
     * In `USER_AUTH` mode these are 'SMS_OTP' | 'EMAIL_OTP' | 'PASSWORD' | 'PASSWORD_SRP' | 'WEB_AUTHN' | 'SELECT_CHALLENGE';
     * @type {Extract<AmplifyNextStep, { signInStep: 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION' }>['availableChallenges']}
     */
    availableChallenges: [],
    /**
     * Information about where the OTP or MFA code is being delivered
     * @type {import('node_modules/@aws-amplify/auth/dist/esm/types').AuthCodeDeliveryDetails<'phone_number'>|null}
     */
    smsCodeDeliveryDetails: null,
    /**
     * Information about where the OTP or MFA code is being delivered
     * @type {import('node_modules/@aws-amplify/auth/dist/esm/types').AuthCodeDeliveryDetails<'email'>|null}
     */
    emailCodeDeliveryDetails: null,
    /**
     * If the user is in a password reset flow
     * @type {import('aws-amplify/auth').ResetPasswordOutput['isPasswordReset']}
     */
    isPasswordReset: false,
    /**
     * If the user is signed in. This will be true if the user has completed the sign-in process.
     * @type {import('aws-amplify/auth').SignInOutput['isSignedIn']}
     */
    isSignedIn: false,
    /**
     * If the user has not completed any necessary account information, it is listed here upon login.
     * @type {Extract<AmplifyNextStep, { signInStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' }>['missingAttributes']}
     */
    missingAttributes: null,
    /**
     * Indicates the current step in the sign-in process.
     * @type {AmplifyNextStep['signInStep']}
     */
    signInStep: 'SIGN_IN',
    /**
     * Indicates the current step in the password reset process.
     * @type {AmplifyNextStep['resetPasswordStep']|null}
     */
    resetPasswordStep: null,
};
/**
 * @param {typeof initialState} state
 * @param {AmplifyNextStep} nextStep
 */
const getSignInStep = (state, nextStep) => {
    if (!nextStep) {
        return 'SIGN_IN';
    }
    return nextStep.signInStep || state.signInStep;
};
/**
 * @param {typeof initialState} state
 * @param {AmplifyNextStep} nextStep
 */
const getAllowedMFATypes = (state, nextStep) => {
    const oldAllowedMFATypes = state.allowedMFATypes || [];
    const newAllowedMFATypes = [...(nextStep?.allowedMFATypes || oldAllowedMFATypes)];
    switch (nextStep?.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
            newAllowedMFATypes.push('SMS');
            break;
        }
        case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
            newAllowedMFATypes.push('TOTP');
            break;
        }
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
            newAllowedMFATypes.push('EMAIL');
            break;
        }
    }
    return newAllowedMFATypes
        .filter(filterUnique)
        .filter(value => DEFAULT_MFA_OPTIONS.includes(value))
        .sort((a, b) => {
            return DEFAULT_MFA_OPTIONS.indexOf(a) - DEFAULT_MFA_OPTIONS.indexOf(b);
        });
};
/**
 * @param {typeof initialState} state
 * @param {AmplifyNextStep} nextStep
 */
export const getAvailableChallenges = (state, nextStep) => {
    switch (nextStep.signInStep) {
        case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION': {
            return nextStep.availableChallenges || [];
        }
    }
    return state.availableChallenges;
};

export default genericReducer(initialState, {
    [AmplifyActions.nextStep]: (
        state,

        /** @type {import('aws-amplify/auth').SignInOutput | import('aws-amplify/auth').ResetPasswordOutput} */ payload
    ) => {
        const { codeDeliveryDetails } = payload?.nextStep || {};
        // We do not want to overwrite any code delivery details because they will always be outstanding and valid for the entire session
        const smsCodeDeliveryDetails =
            codeDeliveryDetails && ['SMS', 'PHONE'].includes(codeDeliveryDetails.deliveryMedium)
                ? /** @type {import('aws-amplify/auth').CodeDeliveryDetails<'phone_number'>} */ (
                      codeDeliveryDetails
                  )
                : state.smsCodeDeliveryDetails;
        const emailCodeDeliveryDetails =
            codeDeliveryDetails && ['EMAIL'].includes(codeDeliveryDetails.deliveryMedium)
                ? /** @type {import('aws-amplify/auth').CodeDeliveryDetails<'email'>} */ (
                      codeDeliveryDetails
                  )
                : state.emailCodeDeliveryDetails;
        return /** @type {typeof initialState} */ ({
            ...state,
            additionalInfo: payload.nextStep.additionalInfo || null,
            allowedMFATypes: getAllowedMFATypes(state, payload.nextStep),
            availableChallenges: getAvailableChallenges(state, payload.nextStep),
            smsCodeDeliveryDetails,
            emailCodeDeliveryDetails,
            isPasswordReset: payload.resetPasswordStep ? !!payload.isPasswordReset : false,
            isSignedIn: !!(payload.isSignedIn ?? state.isSignedIn),
            signInStep: getSignInStep(state, payload.nextStep),
            resetPasswordStep: payload.resetPasswordStep || null,
        });
    },
    [AmplifyActions.verifyPhone]: (
        state,
        /** @type {import('aws-amplify/auth').SendUserAttributeVerificationCodeOutput} */
        payload
    ) => {
        return {
            ...state,
            smsCodeDeliveryDetails: payload,
        };
    },
    [AmplifyActions.updateUserAttributesSuccess]: (
        state,
        /** @type {{ params: Record<string,string>, response: import('aws-amplify/auth').UpdateUserAttributesOutput }} */
        payload
    ) => {
        const { params, response } = payload;
        // Mutate the payload so the new attributes are included for the middleware
        payload.attributes = {};
        /** @type {typeof initialState} */
        const newState = { ...state };
        for (const [key, value] of Object.entries(response)) {
            switch (key) {
                case 'email': {
                    // TODO: When we allow users to update email, the email verification case will need to be handled
                    break;
                }
                case 'phone_number': {
                    const { nextStep } = value;
                    switch (nextStep.updateAttributeStep) {
                        case 'CONFIRM_ATTRIBUTE_WITH_CODE': {
                            newState.smsCodeDeliveryDetails = nextStep.codeDeliveryDetails;
                            payload.attributes.phone = params.phone;
                            // eslint-disable-next-line camelcase
                            payload.attributes.phone_number_verified = false;
                            break;
                        }
                        case 'DONE': {
                            newState.smsCodeDeliveryDetails = null;
                            payload.attributes.phone = params.phone;
                            break;
                        }
                    }
                    break;
                }
                default: {
                    // All other properties do not require verification
                    payload.attributes = {
                        ...payload.attributes,
                        ...mapFromCognitoParams(mapToCognitoParams(params, [key])),
                    };
                    break;
                }
            }
        }
        return newState;
    },
    [AuthActions.signInBegin]: () => initialState,
    [AuthActions.logout]: () => initialState,
});
